import {Box, Menu, MenuItem, Stack, Tooltip} from '@mui/material'
import {useState, useMemo, useEffect} from 'react'
import {useSelector} from 'react-redux'
import {
  StyledSavedFilterDialogHeaderText,
  StyledSavedFiltersIconButton,
  StyledSavedFiltersBookmarkIcon,
  StyledSavedFiltersDialog,
  StyledSavedFiltersDialogContent,
  StyledSavedFiltersFormControl,
  StyledSavedFiltersDialogActions,
  StyledSavedFiltersCancelButton,
  StyledSavedFiltersApplyButton,
  StyledSavedFiltersInfoIcon,
  StyledSavedFiltersStackItem,
  StyledSavedFiltersMoreIconButton,
  StyledTooltipList,
  StyledCircularProgress,
  StyledDivider,
  StyledTooltipListItem,
  StyledFiltersListItem,
  StyledHtmlTooltip
} from '../FilterPanelStyle'
import DialogTitle from '@mui/material/DialogTitle'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import SaveFiltersDialog from './SaveFiltersDialog'
import DeleteFiltersPopup from './DeleteFiltersPopup'
import {useFilterManagement} from '../../../store/hooks/useFilterManagement'
import {selectFilterConfig} from '../../../store/slices/configSlice'
import {selectCustomizations} from '../../../store/slices/filterManagementSlice'
import {getFilterConfig} from '../../../utils/filterManagementHelpers'
import {useSavedFilter} from '../../../store/hooks/savedFilter'
import querySavedFiltersList from '../../../api/queries/SavedFilters/querySavedFilters'
import SaveFilterSnackBar from './SaveFilterSnackBar'

const SavedFiltersPopup = () => {
  // State to control saved filters popup open/close
  const [openSavedFilters, setOpenSavedFilters] = useState(false)
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false)
  // Edit and Delete dialog states open/close
  const [openEdit, setOpenEdit] = useState(false)
  const [openDelete, setOpenDelete] = useState(false)
  const [disable, setDisable] = useState(false)

  // State to control selected saved filter radio button
  const [value, setValue] = useState('')

  // Redux store hooks for saved filters
  const {
    appliedSavedFilterName,
    setCurrentAppliedSavedFilterName,
    savedFilterList,
    setEntireSavedFiltersList,
    currentFilterCombinationName,
    setFilterChangesFromSavedFilter
  } = useSavedFilter()

  // Redux store hooks for filter management
  const {availableFilters, disabledFilters, clearAllFilters} = useFilterManagement()

  // Query to fetch saved filters list
  const {savedFiltersListData, loadingSavedListFilters, refetchSavedFiltersList, errorLoadingSavedFiltersList} =
    querySavedFiltersList()

  // Get filter management hook and config
  const {addMultipleFilters} = useFilterManagement()
  const filterConfig = useSelector(selectFilterConfig)
  const customizations = useSelector(selectCustomizations)

  const notAuthorizedFilterText = `Your saved filters are no longer accessible because your permissions have been updated. Please contact your administrator if you'd like your access to these filters restored.`
  const notNationalFilterText = `Geography or Region filters are not applicable at Nation View. Please select a State to access such filters.`

  // Sync saved filters list from API to Redux store
  useEffect(() => {
    if (savedFiltersListData?.savedFilters) {
      // Transform data to match the expected structure in the store
      const transformedFilters = savedFiltersListData.savedFilters.map((filter) => ({
        name: filter.saved_filter_name,
        filters: filter.saved_filter_values.map((value) => ({
          filterKey: value.FILTER_KEY

          // Additional fields can be added here if needed
        }))
      }))

      if (savedFilterList.length === 0) {
        setCurrentAppliedSavedFilterName('')
        setOpenSavedFilters(false)
      }

      setEntireSavedFiltersList(transformedFilters.sort((a, b) => a.name.localeCompare(b.name)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFiltersListData])

  // Disable Apply button based on conditions
  useEffect(() => {
    const shouldDisable =
      loadingSavedListFilters ||
      errorLoadingSavedFiltersList ||
      savedFilterList.length === 0 ||
      value === '' ||
      value === appliedSavedFilterName

    setDisable(shouldDisable)
  }, [loadingSavedListFilters, errorLoadingSavedFiltersList, savedFilterList.length, value, appliedSavedFilterName])

  // Sync value with appliedSavedFilterName or default to first filter
  useEffect(() => {
    if (appliedSavedFilterName !== '') {
      setValue(appliedSavedFilterName)
    } else if (currentFilterCombinationName !== '') {
      setValue(currentFilterCombinationName)
    } else if (savedFilterList.length > 0) {
      // ✅ Loop through the list to find first enabled filter

      for (const filter of savedFilterList) {
        // Check if this filter has any disabled items
        const hasDisabledItems = filter.filters?.some(
          (item) => disabledFilters.includes(item.filterKey) || !availableFilters.includes(item.filterKey)
        )
        // If this filter is NOT disabled, use it and break
        if (!hasDisabledItems) {
          setValue(filter.name)
          break
        }
      }
    } else {
      setValue('')
    }
  }, [
    appliedSavedFilterName,
    savedFilterList,
    loadingSavedListFilters,
    disabledFilters,
    availableFilters,
    currentFilterCombinationName
  ])

  // To scroll to selected filter when popup opens
  useEffect(() => {
    const targetElement = document.getElementById(value)

    // Scroll to the element
    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: 'smooth', // Makes the scroll smooth
        block: 'center' // Aligns the top of the element to the top of the viewport
      })
    }
  }, [openSavedFilters, value])

  const handleSnackbarToggle = () => {
    setIsSnackBarOpen(!isSnackBarOpen)
  }

  // Format filter info for tooltip display
  const formatFilterInfo = useMemo(() => {
    return (filterItem) => {
      const customName = customizations.filterNames?.[filterItem.filterKey]
      const config = getFilterConfig(filterItem.filterKey, filterConfig)
      const filterName = customName || config?.name || filterItem.filterKey
      return `${filterName}`
    }
  }, [filterConfig, customizations])

  // Handlers to toggle Saved Filters popup, Edit and Delete dialogs
  const toggleSavedFilters = () => {
    setOpenSavedFilters(!openSavedFilters)
  }

  const toggleEdit = () => {
    toggleSavedFilters()
    setOpenEdit(!openEdit)
  }

  const toggleDelete = () => {
    toggleSavedFilters()
    setOpenDelete(!openDelete)
  }

  // Handler for Saved Filters button click
  const handleSavedFiltersClick = () => {
    setOpenSavedFilters(!openSavedFilters)
  }

  // State and handlers for more options menu
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  // Handling more options menu edit/delete
  const handleMoreOptions = (event) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  // Handling edit filter label
  const handleEditFilterLabel = () => {
    // Add your edit logic here
    toggleSavedFilters()
    toggleEdit()
    handleClose()
  }

  // Handling delete saved filter
  const handleOpenDeletePopup = () => {
    // Add your delete logic here
    toggleSavedFilters()
    toggleDelete()
    handleClose()
  }

  // Handler for cancel button click
  const handleCancel = () => {
    handleSavedFiltersClick()
  }

  const handleSubmit = () => {
    // Find the selected saved filter object
    const selectedFilter = savedFilterList.find((f) => f.name === value)

    if (!selectedFilter?.filters) {
      return
    }

    const filterKeysToAdd = selectedFilter.filters.map((f) => f.filterKey).filter(Boolean)

    if (filterKeysToAdd.length > 0) {
      // Clear all pending filter values (for usecases with dynamic filters)
      // clearAllPendingFilterValues()
      clearAllFilters()

      // Mark this filter changes as intended one to avoid clearing appliedSavedFilterName on filter change
      setFilterChangesFromSavedFilter(true)

      // Add the filters from saved filter
      addMultipleFilters(filterKeysToAdd)

      // Set the currently applied saved filter name in the store
      setCurrentAppliedSavedFilterName(value)

      // Show success message
      handleSnackbarToggle()
    }

    handleSavedFiltersClick()
  }

  const handleChange = (event) => {
    setValue(event.target.value)
  }

  // KEYBOARD NAVIGATION HANDLERS
  // Helper function to get enabled filters
  const getEnabledFilters = () => {
    return savedFilterList.filter(
      (filter) =>
        !filter?.filters?.some(
          (item) => disabledFilters.includes(item.filterKey) || !availableFilters.includes(item.filterKey)
        )
    )
  }

  // Helper function to calculate new index for arrow navigation
  const getNewIndex = (currentIndex, enabledFiltersLength, isArrowDown) => {
    if (isArrowDown) {
      return currentIndex === -1 ? 0 : (currentIndex + 1) % enabledFiltersLength
    }
    return currentIndex <= 0 ? enabledFiltersLength - 1 : currentIndex - 1
  }

  const handleKeyDown = (event) => {
    const {key} = event

    if (key === 'Enter') {
      event.preventDefault()
      if (!disable) {
        handleSubmit()
      }
      return
    }

    if (key === 'Escape') {
      event.preventDefault()
      handleCancel()
      return
    }

    if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault()

      const enabledFilters = getEnabledFilters()
      if (enabledFilters.length === 0) return

      const currentIndex = enabledFilters.findIndex((f) => f.name === value)
      const newIndex = getNewIndex(currentIndex, enabledFilters.length, key === 'ArrowDown')

      setValue(enabledFilters[newIndex].name)
    }
  }

  return (
    <>
      {/* SAVED FILTER BUTTON HERE    */}
      <Box alignSelf='center' mt={1.5}>
        <Tooltip title='Saved filters'>
          <span>
            <StyledSavedFiltersIconButton
              aria-label='savedFilters'
              disabled={savedFilterList.length === 0 || loadingSavedListFilters}
              onClick={handleSavedFiltersClick}
              position='fixed'
            >
              <StyledSavedFiltersBookmarkIcon
                fontSize='small'
                disabled={savedFilterList.length === 0 || loadingSavedListFilters}
              />
            </StyledSavedFiltersIconButton>
          </span>
        </Tooltip>
      </Box>

      {/* SAVED FILTERS POPUP HERE */}
      <StyledSavedFiltersDialog
        open={openSavedFilters}
        onClose={handleSavedFiltersClick}
        onKeyDown={handleKeyDown}
        aria-labelledby='saved-filters-list'
        aria-describedby='saved-filters-list-description'
      >
        <DialogTitle id='saved-filters-list'>
          <Stack direction='row' alignItems='center' justifyContent='space-between'>
            <StyledSavedFilterDialogHeaderText>
              Saved filters ({savedFilterList.length})
            </StyledSavedFilterDialogHeaderText>
            <StyledSavedFiltersMoreIconButton
              aria-label='more-options'
              disabled={value === ''}
              onClick={handleMoreOptions}
            >
              <MoreVertOutlinedIcon />
            </StyledSavedFiltersMoreIconButton>
          </Stack>
        </DialogTitle>
        {loadingSavedListFilters && <StyledCircularProgress size={20} />}
        <StyledSavedFiltersDialogContent>
          <StyledSavedFiltersFormControl>
            <RadioGroup
              aria-labelledby='demo-controlled-radio-buttons-group'
              name='controlled-radio-buttons-group'
              value={value}
              onChange={handleChange}
            >
              {savedFilterList.map((filter, index) => (
                <StyledSavedFiltersStackItem key={filter.name + index} direction='row' alignItems='center'>
                  <FormControlLabel
                    id={filter.name}
                    disabled={filter?.filters?.some(
                      (item) => disabledFilters.includes(item.filterKey) || !availableFilters.includes(item.filterKey)
                    )}
                    value={filter.name}
                    control={<Radio />}
                    label={filter.name}
                    onFocus={value}
                  />

                  <StyledHtmlTooltip
                    title={
                      <StyledTooltipList>
                        {filter?.filters?.some((item) => !availableFilters.includes(item.filterKey)) && (
                          <StyledTooltipListItem>{notAuthorizedFilterText}</StyledTooltipListItem>
                        )}
                        {filter?.filters?.some(
                          (item) =>
                            disabledFilters.includes(item.filterKey) && availableFilters.includes(item.filterKey)
                        ) && <StyledTooltipListItem>{notNationalFilterText}</StyledTooltipListItem>}

                        {filter.filters.map((item, idx) => {
                          // ✅ Check if filter is unauthorized or disabled
                          const isDisabled =
                            disabledFilters.includes(item.filterKey) || !availableFilters.includes(item.filterKey)

                          return (
                            <>
                              {isDisabled && <StyledDivider />}
                              <StyledFiltersListItem disabled={isDisabled} key={item.filterKey + idx}>
                                {formatFilterInfo(item)}
                              </StyledFiltersListItem>
                            </>
                          )
                        })}
                      </StyledTooltipList>
                    }
                    placement='left-start'
                  >
                    <StyledSavedFiltersInfoIcon />
                  </StyledHtmlTooltip>
                </StyledSavedFiltersStackItem>
              ))}
            </RadioGroup>
          </StyledSavedFiltersFormControl>
        </StyledSavedFiltersDialogContent>
        <StyledSavedFiltersDialogActions>
          <StyledSavedFiltersCancelButton onClick={handleCancel}>Cancel</StyledSavedFiltersCancelButton>
          <StyledSavedFiltersApplyButton onClick={handleSubmit} variant='contained' disabled={disable}>
            Apply
          </StyledSavedFiltersApplyButton>
        </StyledSavedFiltersDialogActions>
      </StyledSavedFiltersDialog>
      <Menu
        id='basic-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button'
          }
        }}
      >
        <MenuItem onClick={handleEditFilterLabel}>
          <EditOutlinedIcon color='primary' />
          &nbsp; Edit filter label
        </MenuItem>
        <MenuItem onClick={handleOpenDeletePopup}>
          <DeleteOutlineOutlinedIcon color='primary' />
          &nbsp; Delete saved filter
        </MenuItem>
      </Menu>

      {/* EDIT FILTER DIALOG HERE */}
      <SaveFiltersDialog
        open={openEdit}
        onClose={toggleEdit}
        currentFilterName={value}
        data={{title: 'Edit filter label', icon: true}}
        onSuccess={refetchSavedFiltersList}
      />

      {/* DELETE FILTER DIALOG HERE */}
      <DeleteFiltersPopup
        open={openDelete}
        onClose={toggleDelete}
        currentFilterName={value}
        onSuccess={refetchSavedFiltersList}
      />

      <SaveFilterSnackBar
        isSnackBarOpen={isSnackBarOpen}
        handleSnackbarClose={handleSnackbarToggle}
        data={
          <>
            Saved filter <strong>{value}</strong> applied.
          </>
        }
        action='add'
      />
    </>
  )
}

export default SavedFiltersPopup
